const LANGUAGES = {
	en: {
		code: "en",
		name: "English",
		nativeLabel: "English",
		transliterable: false,
		hasLetterCase: true,
		scriptRanges: [[65, 90], [97, 122]],
		defaultFontFamily: "Inter",
		direction: "ltr"
	},
	ml: {
		code: "ml",
		name: "Malayalam",
		nativeLabel: "മലയാളം",
		transliterable: true,
		hasLetterCase: false,
		scriptRanges: [[3328, 3455]],
		defaultFontFamily: "Anek Malayalam",
		direction: "ltr"
	},
	hi: {
		code: "hi",
		name: "Hindi",
		nativeLabel: "हिन्दी",
		transliterable: true,
		hasLetterCase: false,
		scriptRanges: [[2304, 2431], [43232, 43263]],
		defaultFontFamily: "Anek Devanagari",
		direction: "ltr"
	},
	ta: {
		code: "ta",
		name: "Tamil",
		nativeLabel: "தமிழ்",
		transliterable: true,
		hasLetterCase: false,
		scriptRanges: [[2944, 3071]],
		defaultFontFamily: "Anek Tamil",
		direction: "ltr"
	},
	kn: {
		code: "kn",
		name: "Kannada",
		nativeLabel: "ಕನ್ನಡ",
		transliterable: true,
		hasLetterCase: false,
		scriptRanges: [[3200, 3327]],
		defaultFontFamily: "Anek Kannada",
		direction: "ltr"
	},
	te: {
		code: "te",
		name: "Telugu",
		nativeLabel: "తెలుగు",
		transliterable: true,
		hasLetterCase: false,
		scriptRanges: [[3072, 3199]],
		defaultFontFamily: "Anek Telugu",
		direction: "ltr"
	},
	ar: {
		code: "ar",
		name: "Arabic",
		nativeLabel: "العربية",
		transliterable: true,
		hasLetterCase: false,
		scriptRanges: [
			[1536, 1791],
			[1872, 1919],
			[2208, 2303],
			[64336, 65023],
			[65136, 65279]
		],
		defaultFontFamily: "Noto Sans Arabic",
		direction: "rtl"
	}
};
const DEFAULT_LANGUAGE = "en";
function isLanguageCode(value) {
	return Object.hasOwn(LANGUAGES, value);
}
function getLanguage(code) {
	return LANGUAGES[code];
}
function listLanguages() {
	return Object.values(LANGUAGES);
}
/** Languages the IME actually converts — English is typed directly. */
function listTransliterableLanguages() {
	return listLanguages().filter((l) => l.transliterable);
}
/** Is this code point part of the language's target script? */
function isInScript(codePoint, language) {
	return language.scriptRanges.some(([start, end]) => codePoint >= start && codePoint <= end);
}
//#endregion
//#region src/roman-run.ts
const isAsciiLetter = (code) => code >= 65 && code <= 90 || code >= 97 && code <= 122;
/**
* Joiners that belong to a word when they sit *between* letters — `can't`,
* `re-enter` — but not at either end, where they are punctuation.
*/
const isJoiner = (code) => code === 39 || code === 45;
/**
* The maximal roman run containing or ending at `caret`.
*
* Returns `null` when the caret is not in roman text at all — including when
* it sits immediately after a character of the target script, which is how a
* freshly converted word stops the IME from re-querying itself.
*/
function findRomanRun(text, caret, language) {
	if (caret < 0 || caret > text.length) return null;
	let start = caret;
	while (start > 0) {
		const code = text.charCodeAt(start - 1);
		if (isAsciiLetter(code) || isJoiner(code)) start -= 1;
		else break;
	}
	let end = caret;
	while (end < text.length) {
		const code = text.charCodeAt(end);
		if (isAsciiLetter(code) || isJoiner(code)) end += 1;
		else break;
	}
	while (start < end && isJoiner(text.charCodeAt(start))) start += 1;
	while (end > start && isJoiner(text.charCodeAt(end - 1))) end -= 1;
	if (end <= start) return null;
	const run = text.slice(start, end);
	if (![...run].some((char) => isAsciiLetter(char.charCodeAt(0)))) return null;
	if ([...run].some((char) => isInScript(char.codePointAt(0) ?? 0, language))) return null;
	return {
		start,
		end,
		text: run
	};
}
//#endregion
//#region src/client.ts
/**
* The Google Input Tools client.
*
* Request and response shapes follow the endpoint the Burfy mobile app and the
* poster editor already use, so the candidates a user sees here match the ones
* they see there.
*
* The endpoint is undocumented. Everything below assumes it can change shape,
* disappear, or hang: a parse failure yields no candidates, never an
* exception, because **typing must never block on the network**.
*/
const ENDPOINT = "https://inputtools.google.com/request";
/** Cut a request loose rather than let the popup wait on it. */
const DEFAULT_TIMEOUT_MS = 1200;
function buildRequestUrl(text, lang, num) {
	const params = new URLSearchParams({
		text,
		itc: `${lang}-t-i0-und`,
		num: String(num),
		cp: "0",
		cs: "1",
		ie: "utf-8",
		oe: "utf-8",
		app: "burfy-docs"
	});
	return `${ENDPOINT}?${params.toString()}`;
}
/**
* Parse a response.
*
* Success looks like:
*
*   ["SUCCESS", [["namaskaram", ["നമസ്കാരം", "നമസ്ക്കാരം"], [], {}]]]
*
* — the echoed roman source first, then the candidates. Anything else,
* including `["FAIL"]`, an empty array, or a payload with the wrong types
* inside, yields no candidates.
*/
function parseResponse(payload) {
	if (!Array.isArray(payload) || payload[0] !== "SUCCESS") return null;
	const results = payload[1];
	if (!Array.isArray(results) || results.length === 0) return null;
	const first = results[0];
	if (!Array.isArray(first)) return null;
	const source = typeof first[0] === "string" ? first[0] : null;
	if (source === null) return null;
	const raw = first[1];
	const candidates = Array.isArray(raw) ? raw.filter((entry) => typeof entry === "string" && entry.length > 0) : [];
	const seen = /* @__PURE__ */ new Set();
	return {
		source,
		candidates: candidates.filter((entry) => {
			if (seen.has(entry)) return false;
			seen.add(entry);
			return true;
		})
	};
}
/**
* Fetch candidates for one roman word.
*
* Never throws and never rejects: a network failure, a timeout, an abort or a
* malformed payload all produce an empty candidate list. The caller's job is
* to keep the user typing, not to surface transport errors.
*/
async function transliterate(text, lang, options = {}) {
	const trimmed = text.trim();
	const empty = {
		source: trimmed,
		candidates: []
	};
	if (!trimmed) return empty;
	const { num = 5, signal, fetchImpl = fetch } = options;
	try {
		const response = await fetchImpl(buildRequestUrl(trimmed, lang, num), signal ? { signal } : void 0);
		if (!response.ok) return empty;
		return parseResponse(await response.json()) ?? empty;
	} catch {
		return empty;
	}
}
//#endregion
//#region src/cache.ts
/**
* A least-recently-used cache for candidate lists.
*
* Re-typing a word — which people do constantly — should cost nothing, and a
* cache hit is what makes the popup appear instantly instead of a network
* round-trip later.
*/
const DEFAULT_CAPACITY = 500;
var LruCache = class {
	#entries = /* @__PURE__ */ new Map();
	#capacity;
	#hits = 0;
	#misses = 0;
	constructor(capacity = 500) {
		if (!Number.isInteger(capacity) || capacity < 1) throw new RangeError(`LruCache capacity must be a positive integer (got ${capacity})`);
		this.#capacity = capacity;
	}
	get size() {
		return this.#entries.size;
	}
	get stats() {
		return {
			hits: this.#hits,
			misses: this.#misses
		};
	}
	get(key) {
		const value = this.#entries.get(key);
		if (value === void 0) {
			this.#misses += 1;
			return;
		}
		this.#entries.delete(key);
		this.#entries.set(key, value);
		this.#hits += 1;
		return value;
	}
	has(key) {
		return this.#entries.has(key);
	}
	set(key, value) {
		if (this.#entries.has(key)) this.#entries.delete(key);
		this.#entries.set(key, value);
		while (this.#entries.size > this.#capacity) {
			const oldest = this.#entries.keys().next();
			if (oldest.done) break;
			this.#entries.delete(oldest.value);
		}
	}
	clear() {
		this.#entries.clear();
		this.#hits = 0;
		this.#misses = 0;
	}
};
/** Cache key for one word in one language. */
function candidateKey(lang, roman) {
	return `${lang}:${roman.toLowerCase()}`;
}
//#endregion
//#region src/learning.ts
/**
* Ceiling on a single candidate's count.
*
* Without it, a word chosen a thousand times could never be displaced by a
* genuine change of preference — the user would fight their own history.
*/
const MAX_LEARNED_COUNT = 50;
function learningKey(lang, roman) {
	return `${lang}:${roman.toLowerCase()}`;
}
/** Apply one choice to an entry. Pure, so the store implementation is trivial. */
function recordChoice(entry, candidate, cap = 50) {
	const current = entry?.[candidate] ?? 0;
	return {
		...entry,
		[candidate]: Math.min(current + 1, cap)
	};
}
/**
* Merge and order the candidates.
*
* Deterministic and stable: learned choices by count, then the source's own
* order, ties broken by that order and never by anything time-dependent. A
* ranking that could differ between two runs on the same inputs would make
* the popup jump under the user's fingers.
*/
function rankCandidates({ remote, learned, literal }) {
	const remoteIndex = new Map(remote.map((candidate, index) => [candidate, index]));
	const ordered = [];
	const seen = /* @__PURE__ */ new Set();
	const push = (candidate) => {
		if (!candidate || seen.has(candidate)) return;
		seen.add(candidate);
		ordered.push(candidate);
	};
	const learnedEntries = Object.entries(learned ?? {}).filter(([, count]) => count > 0);
	learnedEntries.sort(([aCandidate, aCount], [bCandidate, bCount]) => {
		if (aCount !== bCount) return bCount - aCount;
		return (remoteIndex.get(aCandidate) ?? Number.MAX_SAFE_INTEGER) - (remoteIndex.get(bCandidate) ?? Number.MAX_SAFE_INTEGER);
	});
	for (const [candidate] of learnedEntries) push(candidate);
	for (const candidate of remote) push(candidate);
	if (literal) push(literal);
	return ordered;
}
//#endregion
//#region src/keys.ts
const PASS = { kind: "pass" };
/**
* `Ctrl/Cmd+G` — the chord Google Input Tools users already have in their
* fingers. It both toggles the language and re-opens candidates on a word
* that was already converted.
*/
function isReopenChord(event) {
	return (event.ctrlKey === true || event.metaKey === true) && event.key.toLowerCase() === "g";
}
/**
* Resolve a keystroke while the candidate popup is open.
*
* `open` is false when there is nothing to choose from; only the re-open
* chord is meaningful then.
*/
function resolveImeKey(event, open) {
	if (event.isComposing) return PASS;
	if (isReopenChord(event)) return { kind: "reopen" };
	if (!open) return PASS;
	if (event.ctrlKey || event.metaKey || event.altKey) return PASS;
	switch (event.key) {
		case " ": return {
			kind: "accept",
			trailing: " "
		};
		case "Enter":
		case "Tab": return {
			kind: "accept",
			trailing: ""
		};
		case "Escape": return { kind: "dismiss" };
		case "ArrowDown": return {
			kind: "move",
			delta: 1
		};
		case "ArrowUp": return {
			kind: "move",
			delta: -1
		};
	}
	if (!event.shiftKey && /^[1-9]$/.test(event.key)) return {
		kind: "accept-index",
		index: Number(event.key) - 1
	};
	return PASS;
}
/** Wrap an index into range, so arrowing past either end cycles. */
function moveHighlight(current, delta, count) {
	if (count <= 0) return 0;
	return ((current + delta) % count + count) % count;
}
//#endregion
//#region src/speech/engine-choice.ts
const PREFERENCES = [
	"auto",
	"off",
	"webspeech",
	"cloud"
];
function isSpeechEnginePreference(value) {
	return PREFERENCES.includes(value);
}
/**
* Read a preference from configuration — an env var, a localStorage entry.
*
* Anything unrecognised falls back rather than throwing: a typo in a deploy
* variable should not take voice typing down, and it certainly should not take
* the editor down.
*/
function parseEnginePreference(value, fallback = "auto") {
	if (!value) return fallback;
	const normalised = value.trim().toLowerCase();
	return isSpeechEnginePreference(normalised) ? normalised : fallback;
}
/**
* Resolve the preference against the engines actually compiled in.
*
* A named engine that cannot run yields `unavailable` rather than quietly
* substituting another: during a ramp, "we asked for cloud and got the browser
* instead" is precisely the outcome that would poison the comparison.
*/
function selectSpeechEngine(preference, engines) {
	if (preference === "off") return {
		engine: null,
		reason: "disabled"
	};
	if (preference === "auto") {
		const available = engines.find((engine) => engine.isAvailable());
		return available ? { engine: available } : {
			engine: null,
			reason: "none"
		};
	}
	const named = engines.find((engine) => engine.id === preference);
	if (named?.isAvailable() === true) return { engine: named };
	return {
		engine: null,
		reason: "unavailable"
	};
}
//#endregion
//#region src/speech/locales.ts
/**
* Language → speech locale.
*
* Data, not code, and deliberately the *same* language the user picked for
* typing: one control drives transliteration and dictation both, so there is
* never a document where the two disagree about what is being written.
*/
/**
* Regional default per language.
*
* `en-IN` rather than `en-US`: the people typing Malayalam in this editor are,
* overwhelmingly, the people speaking Indian English into it. Anyone who wants
* otherwise passes an override — configuration, not a release.
*/
const DEFAULT_SPEECH_LOCALES = {
	en: "en-IN",
	ml: "ml-IN",
	hi: "hi-IN",
	ta: "ta-IN",
	kn: "kn-IN",
	te: "te-IN",
	ar: "ar-SA"
};
/** A BCP-47 tag: `ml`, `en-IN`, `sr-Latn-RS`. Loose on purpose — engines vary. */
const BCP47 = /^[A-Za-z]{2,8}(-[A-Za-z0-9]{2,8})*$/;
function isSpeechLocale(value) {
	return BCP47.test(value);
}
/**
* The locale to recognise this language in.
*
* A malformed override is ignored rather than passed on: engines react to a
* bad tag by throwing or by silently recognising English, and neither is a
* thing to discover mid-sentence.
*/
function speechLocale(code, overrides) {
	const override = overrides?.[code];
	if (override !== void 0 && isSpeechLocale(override)) return override;
	return DEFAULT_SPEECH_LOCALES[code];
}
/** Every language in the table can be dictated; none is text-entry only. */
function listDictationLocales() {
	return Object.values(LANGUAGES).map((language) => ({
		code: language.code,
		locale: DEFAULT_SPEECH_LOCALES[language.code],
		label: language.nativeLabel
	}));
}
//#endregion
//#region src/speech/commands.ts
/**
* Spoken commands — punctuation and breaks.
*
* Two tables, both pure data. One maps a *phrase* to the thing it means
* ("full stop" → end-of-sentence); the other maps that meaning to the glyph
* the language actually writes it with. Keeping them apart is what lets a
* bilingual speaker say "comma" in the middle of an Arabic sentence and get
* `،` rather than `,` — the phrase is English, the punctuation is not.
*/
const LATIN_PUNCTUATION = {
	period: ".",
	comma: ",",
	question: "?",
	exclamation: "!",
	colon: ":",
	semicolon: ";"
};
const PUNCTUATION = {
	en: LATIN_PUNCTUATION,
	ml: LATIN_PUNCTUATION,
	hi: {
		...LATIN_PUNCTUATION,
		period: "।"
	},
	ta: LATIN_PUNCTUATION,
	kn: LATIN_PUNCTUATION,
	te: LATIN_PUNCTUATION,
	ar: {
		...LATIN_PUNCTUATION,
		comma: "،",
		question: "؟",
		semicolon: "؛"
	}
};
const P = (slot) => ({
	kind: "punctuation",
	slot
});
const NEWLINE = { kind: "newline" };
const PARAGRAPH = { kind: "paragraph" };
/**
* English phrases, available in **every** language.
*
* Someone dictating Malayalam routinely says "comma" in English, and the
* scripts cannot collide: an Indic transcript contains no Latin tokens, so
* offering these costs nothing and saves a switch back to English.
*/
const ENGLISH_PHRASES = [
	["full stop", P("period")],
	["period", P("period")],
	["comma", P("comma")],
	["question mark", P("question")],
	["exclamation mark", P("exclamation")],
	["exclamation point", P("exclamation")],
	["colon", P("colon")],
	["semicolon", P("semicolon")],
	["semi colon", P("semicolon")],
	["new line", NEWLINE],
	["newline", NEWLINE],
	["next line", NEWLINE],
	["new paragraph", PARAGRAPH],
	["next paragraph", PARAGRAPH]
];
/** Native phrases, per language. Adding one is a row, not a code change. */
const NATIVE_PHRASES = {
	ml: [
		["പൂർണ്ണവിരാമം", P("period")],
		["പൂർണവിരാമം", P("period")],
		["കോമ", P("comma")],
		["ചോദ്യചിഹ്നം", P("question")],
		["ആശ്ചര്യചിഹ്നം", P("exclamation")],
		["പുതിയ വരി", NEWLINE],
		["പുതിയ ഖണ്ഡിക", PARAGRAPH]
	],
	hi: [
		["पूर्ण विराम", P("period")],
		["पूर्णविराम", P("period")],
		["अल्प विराम", P("comma")],
		["कॉमा", P("comma")],
		["प्रश्न चिह्न", P("question")],
		["विस्मयादिबोधक चिह्न", P("exclamation")],
		["नई लाइन", NEWLINE],
		["नया पैराग्राफ", PARAGRAPH],
		["नया अनुच्छेद", PARAGRAPH]
	],
	ta: [
		["முற்றுப்புள்ளி", P("period")],
		["காற்புள்ளி", P("comma")],
		["கேள்விக்குறி", P("question")],
		["ஆச்சரியக்குறி", P("exclamation")],
		["புதிய வரி", NEWLINE],
		["புதிய பத்தி", PARAGRAPH]
	],
	kn: [
		["ಪೂರ್ಣವಿರಾಮ", P("period")],
		["ಅಲ್ಪವಿರಾಮ", P("comma")],
		["ಪ್ರಶ್ನಾರ್ಥಕ ಚಿಹ್ನೆ", P("question")],
		["ಆಶ್ಚರ್ಯಸೂಚಕ ಚಿಹ್ನೆ", P("exclamation")],
		["ಹೊಸ ಸಾಲು", NEWLINE],
		["ಹೊಸ ಪ್ಯಾರಾ", PARAGRAPH]
	],
	te: [
		["పూర్ణవిరామం", P("period")],
		["కామా", P("comma")],
		["ప్రశ్నార్థకం", P("question")],
		["ఆశ్చర్యార్థకం", P("exclamation")],
		["కొత్త లైన్", NEWLINE],
		["కొత్త పేరా", PARAGRAPH]
	],
	ar: [
		["نقطة", P("period")],
		["فاصلة", P("comma")],
		["علامة استفهام", P("question")],
		["علامة تعجب", P("exclamation")],
		["نقطتان", P("colon")],
		["سطر جديد", NEWLINE],
		["فقرة جديدة", PARAGRAPH]
	]
};
const tableCache = /* @__PURE__ */ new Map();
function compile(language) {
	const cached = tableCache.get(language);
	if (cached) return cached;
	const punctuation = PUNCTUATION[language];
	const table = /* @__PURE__ */ new Map();
	const add = ([phrase, target]) => {
		table.set(phrase.toLowerCase(), target.kind === "punctuation" ? {
			kind: "punctuation",
			text: punctuation[target.slot]
		} : target);
	};
	for (const entry of ENGLISH_PHRASES) add(entry);
	for (const entry of NATIVE_PHRASES[language] ?? []) add(entry);
	let maxWords = 1;
	for (const phrase of table.keys()) maxWords = Math.max(maxWords, phrase.split(" ").length);
	const compiled = {
		phrases: table,
		maxWords
	};
	tableCache.set(language, compiled);
	return compiled;
}
/** Every phrase this language understands, resolved to real glyphs. */
function commandTable(language) {
	return compile(language).phrases;
}
/** Characters an engine tacks onto a word, stripped only to *look up* a phrase. */
const EDGE_PUNCTUATION = /^[.,!?;:"'`।،؟؛]+|[.,!?;:"'`।،؟؛]+$/g;
function lookupKey(tokens) {
	return tokens.map((token) => token.replace(EDGE_PUNCTUATION, "")).join(" ").toLowerCase();
}
/**
* Split a transcript into text and commands.
*
* Longest phrase first, over whole tokens only — so "comma" between words is
* punctuation while "commanding" is left entirely alone, and "new paragraph"
* is never mistaken for the word "new" followed by a noun.
*/
function parseDictation(transcript, language) {
	const tokens = transcript.split(/\s+/u).filter((token) => token.length > 0);
	if (tokens.length === 0) return [];
	const { phrases, maxWords } = compile(language);
	const segments = [];
	let pending = [];
	const flush = () => {
		if (pending.length === 0) return;
		segments.push({
			kind: "text",
			text: pending.join(" ")
		});
		pending = [];
	};
	for (let index = 0; index < tokens.length;) {
		let matched = false;
		for (let width = Math.min(maxWords, tokens.length - index); width >= 1; width -= 1) {
			const key = lookupKey(tokens.slice(index, index + width));
			const action = key ? phrases.get(key) : void 0;
			if (!action) continue;
			flush();
			segments.push(action);
			index += width;
			matched = true;
			break;
		}
		if (!matched) {
			const token = tokens[index];
			if (token !== void 0) pending.push(token);
			index += 1;
		}
	}
	flush();
	return segments;
}
/** True when the language writes with letter case — i.e. capitalising means something. */
function hasLetterCase(language) {
	return getLanguage(language).hasLetterCase;
}
//#endregion
//#region src/speech/dictation.ts
/**
* Turning a transcript into something insertable.
*
* The engine hands over bare words. Everything that makes them read like
* writing — a space before the next word but not before a comma, a capital
* after a full stop but never in Malayalam — is decided here, as a pure
* function of the transcript and the character the caret is sitting after.
*
* It lives in this package, not in the editor, for the usual reason: every
* rule in it is a sentence a person could argue with, and arguing with it
* should cost a unit test rather than a microphone.
*/
/** Sentence enders across the languages in the table, danda and Arabic included. */
const SENTENCE_END = /[.!?।؟]/u;
/** After these, a following word needs no space of its own. */
const NO_SPACE_AFTER = /[\s([{«“„¿¡\-–—/]/u;
function capitaliseFirst(text) {
	const [first, ...rest] = [...text];
	if (first === void 0) return text;
	return first.toLocaleUpperCase() + rest.join("");
}
/**
* Plan the insertion for one final transcript.
*
* Interim results never come through here: they are painted as a ghost and
* thrown away, so spacing them would be work done to be discarded.
*/
function planInsertion(transcript, language, context) {
	const segments = parseDictation(transcript, language);
	if (segments.length === 0) return [];
	const cased = getLanguage(language).hasLetterCase;
	const steps = [];
	let previous = context.precedingChar;
	/** True at the start of a document, a paragraph, or a new sentence. */
	let sentenceStart = previous === null || SENTENCE_END.test(previous);
	for (const segment of segments) {
		if (segment.kind === "newline" || segment.kind === "paragraph") {
			steps.push({ kind: segment.kind });
			previous = null;
			sentenceStart = true;
			continue;
		}
		if (segment.kind === "punctuation") {
			steps.push({
				kind: "text",
				text: segment.text
			});
			previous = segment.text;
			sentenceStart = SENTENCE_END.test(segment.text);
			continue;
		}
		const needsSpace = previous !== null && !NO_SPACE_AFTER.test(previous);
		const body = cased && sentenceStart ? capitaliseFirst(segment.text) : segment.text;
		const text = needsSpace ? ` ${body}` : body;
		steps.push({
			kind: "text",
			text
		});
		previous = text.slice(-1);
		sentenceStart = false;
	}
	return steps;
}
/** The steps as one string — handy in tests, and for any flat-text consumer. */
function stepsToText(steps) {
	return steps.map((step) => {
		if (step.kind === "text") return step.text;
		return step.kind === "newline" ? "\n" : "\n\n";
	}).join("");
}
//#endregion
export { DEFAULT_CAPACITY, DEFAULT_LANGUAGE, DEFAULT_SPEECH_LOCALES, DEFAULT_TIMEOUT_MS, LANGUAGES, LruCache, MAX_LEARNED_COUNT, buildRequestUrl, candidateKey, commandTable, findRomanRun, getLanguage, hasLetterCase, isInScript, isLanguageCode, isReopenChord, isSpeechEnginePreference, isSpeechLocale, learningKey, listDictationLocales, listLanguages, listTransliterableLanguages, moveHighlight, parseDictation, parseEnginePreference, parseResponse, planInsertion, rankCandidates, recordChoice, resolveImeKey, selectSpeechEngine, speechLocale, stepsToText, transliterate };
